import type { ReactNode } from "react";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";

type Tone = "positive" | "negative" | "muted";

const STATUS_KEYS: Record<string, TranslationKey> = {
  new: "support.status.new",
  open: "support.status.open",
  pending: "support.status.pending",
  resolved: "support.status.resolved",
  closed: "support.status.closed",
};

const PRIORITY_KEYS: Record<string, TranslationKey> = {
  low: "support.priority.low",
  medium: "support.priority.medium",
  high: "support.priority.high",
  urgent: "support.priority.urgent",
};

const CHANNEL_KEYS: Record<string, TranslationKey> = {
  portal: "support.channel.portal",
  email: "support.channel.email",
  chat: "support.channel.chat",
  phone: "support.channel.phone",
  api: "support.channel.api",
};

const ORIENTATION_KEYS: Record<string, TranslationKey> = {
  inbound: "support.orientation.inbound",
  outbound: "support.orientation.outbound",
  internal: "support.orientation.internal",
  system: "support.orientation.system",
};

const SLA_KIND_KEYS: Record<string, TranslationKey> = {
  first_response: "support.sla.kind.firstResponse",
  resolution: "support.sla.kind.resolution",
};

const SLA_STATUS_KEYS: Record<string, TranslationKey> = {
  active: "support.sla.status.active",
  met: "support.sla.status.met",
  breached: "support.sla.status.breached",
  canceled: "support.sla.status.canceled",
};

function statusTone(status: string): Tone {
  if (status === "resolved" || status === "open") {
    return "positive";
  }
  return "muted";
}

function priorityTone(priority: string): Tone {
  if (priority === "high" || priority === "urgent") {
    return "negative";
  }
  return "muted";
}

function orientationTone(orientation: string): Tone {
  if (orientation === "outbound") {
    return "positive";
  }
  if (orientation === "internal") {
    return "negative";
  }
  return "muted";
}

function slaStatusTone(status: string): Tone {
  if (status === "met") {
    return "positive";
  }
  if (status === "breached") {
    return "negative";
  }
  return "muted";
}

function Badge({
  tone,
  label,
}: {
  tone: Tone;
  label: string;
}): ReactNode {
  return <span className={`badge badge--${tone}`}>{label}</span>;
}

export function SupportStatusBadge({ status }: { status: string }): ReactNode {
  const { t } = useConfig();
  const key = STATUS_KEYS[status];
  return <Badge tone={statusTone(status)} label={key === undefined ? status : t(key)} />;
}

export function SupportPriorityBadge({ priority }: { priority: string }): ReactNode {
  const { t } = useConfig();
  const key = PRIORITY_KEYS[priority];
  return <Badge tone={priorityTone(priority)} label={key === undefined ? priority : t(key)} />;
}

export function SupportChannelLabel({ channel }: { channel: string }): ReactNode {
  const { t } = useConfig();
  const key = CHANNEL_KEYS[channel];
  return <span>{key === undefined ? channel : t(key)}</span>;
}

export function SupportOrientationBadge({
  orientation,
}: {
  orientation: string;
}): ReactNode {
  const { t } = useConfig();
  const key = ORIENTATION_KEYS[orientation];
  return (
    <Badge tone={orientationTone(orientation)} label={key === undefined ? orientation : t(key)} />
  );
}

export function SupportSlaKindLabel({ kind }: { kind: string }): ReactNode {
  const { t } = useConfig();
  const key = SLA_KIND_KEYS[kind];
  return <span>{key === undefined ? kind : t(key)}</span>;
}

export function SupportSlaStatusBadge({ status }: { status: string }): ReactNode {
  const { t } = useConfig();
  const key = SLA_STATUS_KEYS[status];
  return <Badge tone={slaStatusTone(status)} label={key === undefined ? status : t(key)} />;
}